import { type ApolloError, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import type { ActionContext } from 'js/lib/ActionContext';
import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';

import getInjectableActivitiesQuery from 'bundles/ondemand-common/activity-injection/queries/GetInjectableActivities.graphql';
import type {
  GetInjectableActivitiesQuery,
  GetInjectableActivitiesQueryVariables,
  GetInjectableActivities_ElementsFragment as InjectableActivity,
} from 'bundles/ondemand-common/activity-injection/queries/__generated__/GetInjectableActivities';
import { useIsInjectionEnabledForCourse } from 'bundles/ondemand-common/activity-injection/useIsInjectionEnabledForCourse';

export const setInjectableActivitiesAction = (
  actionContext: ActionContext,
  { activities }: { activities: InjectableActivity[] }
) => {
  actionContext.dispatch('SET_INJECTABLE_ACTIVITIES', { activities });
};

/**
 * Checks if injectable activities feature is enabled for a given course.
 * Fetches injectable activities for a given course from a GraphQL endpoint.
 * Sets the injectable activities in the CourseStore.
 *
 * @param courseId - The id of the course to get the injectable activities for.
 * @returns a loading and error state.
 */
export const useLoadInjectableActivities = (
  courseId: string
): { loading: boolean; error?: ApolloError; injectableActivities: InjectableActivity[] } => {
  const { isEnabled: isInjectionEnabledForCourse, loading: isInjectionEnabledForCourseLoading } =
    useIsInjectionEnabledForCourse(courseId);

  const skip = isInjectionEnabledForCourseLoading || !isInjectionEnabledForCourse;

  const executeAction = useFluxibleAction();

  const onCompleted = (data: GetInjectableActivitiesQuery) => {
    const activities = data.InjectableActivities?.queryMyInjectableActivitiesForCurrentBranch.elements ?? [];
    executeAction(setInjectableActivitiesAction, { activities });
  };

  const {
    loading: isInjectableActivitiesLoading,
    error,
    data,
  } = useQuery<GetInjectableActivitiesQuery, GetInjectableActivitiesQueryVariables>(getInjectableActivitiesQuery, {
    variables: { courseId, limit: 1000 },
    context: { clientName: 'gatewayGql' },
    skip,
    onError: Sentry.captureException,
    onCompleted,
  });

  return {
    loading: isInjectionEnabledForCourseLoading || isInjectableActivitiesLoading,
    injectableActivities: data?.InjectableActivities?.queryMyInjectableActivitiesForCurrentBranch.elements ?? [],
    error,
  };
};
